import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import './rush.css';
import './roles.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { ROLES, ROLE_ICONS, ROLE_TAGLINES } from './rolesData';
import WaitlistForm from './WaitlistForm';

const Hero = ({ role }) => (
  <section className="cust-hero role-hero">
    <div className="hero-mesh"></div>
    <div className="rs-container hero-anim" style={{ position: 'relative', zIndex: 1 }}>
      <div className="role-hero-text">
        <span className="hero-eyebrow"><span className="dot"></span>{role.eyebrow}</span>
        <h1 className="cust-h1 role-h1">{role.hero.h1}</h1>
        <p className="cust-lead role-lead">{role.hero.lead}</p>
        <div style={{ marginTop: 28 }}>
          <WaitlistForm source={`role-${role.slug}-hero`} inputWidth={280} className="hero-cta-row" />
        </div>
        <div className="role-hero-meta">
          {role.hero.meta.map((m) => <span key={m} className="role-meta-chip">{m}</span>)}
        </div>
      </div>
    </div>
  </section>
);

const Outcomes = ({ role }) => (
  <section className="section-tight" style={{ paddingTop: 24 }}>
    <div className="rs-container">
      <div className="role-outcomes reveal stagger">
        {role.outcome.map((o) => (
          <div className="role-outcome" key={o.label}>
            <div className="role-outcome-num">{o.num}</div>
            <div className="role-outcome-label">{o.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PainSolution = ({ role }) => (
  <section className="section">
    <div className="rs-container">
      <div className="section-head reveal" style={{ textAlign: 'left', margin: '0 0 40px', maxWidth: 720 }}>
        <div className="eyebrow">What changes</div>
        <h2 style={{ fontSize: 40 }}>The three things {role.label.toLowerCase()} stop fighting.</h2>
      </div>
      <div className="ps-list reveal stagger">
        {role.painSolution.map((ps, i) => (
          <div className="ps-row" key={i}>
            <div className="ps-num">0{i + 1}</div>
            <div className="ps-pain">
              <div className="ps-tag pain">Before</div>
              <p>{ps.pain}</p>
            </div>
            <div className="ps-arrow" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M4 11h14m0 0l-5-5m5 5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <div className="ps-solution">
              <div className="ps-tag fix">With Rush Studio</div>
              <p>{ps.solution}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Workflow = ({ role }) => (
  <section className="section" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
    <div className="rs-container">
      <div className="section-head reveal" style={{ textAlign: 'left', margin: '0 0 40px', maxWidth: 'none' }}>
        <div className="eyebrow">Workflow</div>
        <h2 style={{ fontSize: 36 }}>How it works for {role.label.toLowerCase()}.</h2>
      </div>
      <div className="role-steps reveal stagger">
        {role.workflow.map((s, i) => (
          <div className="role-step" key={i}>
            <div className="role-step-num">{i + 1}</div>
            <h4>{s.t}</h4>
            <p>{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Features = ({ role }) => (
  <section className="section">
    <div className="rs-container">
      <div className="section-head reveal" style={{ textAlign: 'left', margin: '0 0 40px', maxWidth: 720 }}>
        <div className="eyebrow">Features that matter</div>
        <h2 style={{ fontSize: 36 }}>Built for the way {role.label.toLowerCase()} actually work.</h2>
      </div>
      <div className="role-feat-grid reveal stagger">
        {role.features.map((f, i) => (
          <div className="role-feat-card" key={i}>
            <h3>{f.t}</h3>
            <p>{f.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Quote = ({ role }) => (
  <section className="section-tight">
    <div className="rs-container">
      <figure className="role-quote reveal">
        <svg className="quote-mark" width="48" height="38" viewBox="0 0 28 22" fill="none" aria-hidden="true">
          <path d="M0 22V11.5C0 5.2 4.2 0.6 10.6 0v4.4C7.3 5 5.4 7.6 5.4 10.7H10.6V22H0zm17.4 0V11.5C17.4 5.2 21.6 0.6 28 0v4.4c-3.3.6-5.2 3.2-5.2 6.3H28V22H17.4z" fill="currentColor" />
        </svg>
        <blockquote className="role-quote-text">"{role.quote.text}"</blockquote>
        <figcaption className="role-quote-author">
          <span className="av av-1" style={{ width: 44, height: 44, fontSize: 14 }}>{role.quote.avatar}</span>
          <div>
            <div className="role-quote-name">{role.quote.name}</div>
            <div className="role-quote-role">{role.quote.role}</div>
          </div>
        </figcaption>
        <div className="role-quote-tags">
          {role.relatedTags.map((t) => <span className="case-tag" key={t}>{t}</span>)}
        </div>
      </figure>
    </div>
  </section>
);

const OtherRoles = ({ activeSlug }) => {
  const others = Object.values(ROLES).filter((r) => r.slug !== activeSlug);
  return (
    <section className="section">
      <div className="rs-container">
        <div className="section-head reveal" style={{ textAlign: 'left', margin: '0 0 32px', maxWidth: 720 }}>
          <div className="eyebrow">Other roles</div>
          <h2 style={{ fontSize: 30 }}>Built for the rest of your team too.</h2>
        </div>
        <div className="other-roles reveal stagger">
          {others.map((r) => (
            <Link className="other-role-card" key={r.slug} to={`/roles/${r.slug}`}>
              <div className="other-role-ic">{ROLE_ICONS[r.slug]}</div>
              <div className="other-role-body">
                <div className="other-role-name">{r.label}</div>
                <div className="other-role-sub">{ROLE_TAGLINES[r.slug]}</div>
              </div>
              <span className="other-role-arrow">→</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTA = ({ role }) => (
  <section className="section">
    <div className="rs-container">
      <div className="final-cta reveal reveal-scale">
        <h2>See Rush Studio in your stack.</h2>
        <p>Join the waitlist and our team will help you set up a project from your own files — usually in under a week.</p>
        <WaitlistForm source={`role-${role.slug}-final`} />
      </div>
    </div>
  </section>
);

const RushRolePage = () => {
  const { slug } = useParams();
  const role = ROLES[slug];
  useRushReveal();

  if (!role) return <Navigate to="/customers" replace />;

  return (
    <div className="rs">
      <RushNav active="customers" />
      <Hero role={role} />
      <Outcomes role={role} />
      <PainSolution role={role} />
      <Workflow role={role} />
      <Features role={role} />
      <Quote role={role} />
      <OtherRoles activeSlug={role.slug} />
      <CTA role={role} />
      <RushFooter />
    </div>
  );
};

export default RushRolePage;
